/**
 * Formats a Firestore date (Timestamp or regular date) to a localized string
 * @param date - The date to format (can be Firestore Timestamp, Date object, string, or number)
 * @param options - Intl.DateTimeFormatOptions for formatting
 * @param locale - Locale for formatting (default: 'en-GB')
 * @returns Formatted date string or fallback value
 */
export const formatFirestoreDate = (
  date: any,
  options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  },
  locale = 'en-GB',
  fallback = 'Invalid Date',
): string => {
  if (!date) return fallback;

  try {
    // Check if it's a Firestore Timestamp with toDate method
    if (date && typeof date === 'object' && typeof date.toDate === 'function') {
      return date.toDate().toLocaleDateString(locale, options);
    }

    // Check if it's a Firestore Timestamp-like object with seconds field
    if (date && typeof date === 'object' && 'seconds' in date) {
      const milliseconds = date.seconds * 1000 + (date.nanoseconds || 0) / 1000000;
      return new Date(milliseconds).toLocaleDateString(locale, options);
    }

    // Check if it's a Firestore Timestamp-like object with _seconds field (alternative format)
    if (date && typeof date === 'object' && '_seconds' in date) {
      const milliseconds = date._seconds * 1000 + (date._nanoseconds || 0) / 1000000;
      return new Date(milliseconds).toLocaleDateString(locale, options);
    }

    // Try to parse as regular date string or number
    const parsedDate = new Date(date);
    if (!isNaN(parsedDate.getTime())) {
      return parsedDate.toLocaleDateString(locale, options);
    }

    return fallback;
  } catch (error) {
    // Silently return fallback without logging error
    return fallback;
  }
};

/**
 * Formats a Firestore date to datetime string
 * @param date - The date to format
 * @returns Formatted datetime string
 */
export const formatFirestoreDateTime = (
  date: any,
  locale = 'en-GB',
  fallback = 'Invalid Date',
): string => formatFirestoreDate(
  date,
  {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  },
  locale,
  fallback,
);

/**
 * Checks if a date is a valid timestamp or date
 * @param date - The date to check
 * @returns boolean indicating if the date is valid
 */
export const isValidFirestoreDate = (date: any): boolean => {
  if (!date) return false;

  try {
    // Check Firestore Timestamp
    if (date && typeof date === 'object' && typeof date.toDate === 'function') {
      const d = date.toDate();
      return !isNaN(d.getTime());
    }

    // Check Timestamp-like object
    if (date && typeof date === 'object' && ('seconds' in date || '_seconds' in date)) {
      return true;
    }

    // Check regular date
    const parsedDate = new Date(date);
    return !isNaN(parsedDate.getTime());
  } catch {
    return false;
  }
};
